import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import * as React from 'react';
import {
    Cell,
    Column,
    type ContextValue,
    type Key,
    Row,
    type RowProps,
    type Selection,
    Table,
    TableBody,
    TableHeader,
    type TableProps,
    useContextProps,
} from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';

import { Actions } from '../Actions';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { EmptyState } from '../EmptyState';
import { Heading } from '../Heading';
import { LoadingPanel } from '../Loading';
import { PaginationContext } from '../Pagination';
import type { Href } from '../Router';
import { HeadlessTextInputContext } from '../TextInput';
import { SearchContext, type SearchStateProps, useSearchState } from './Search';
import type {
    DataTableColumns,
    DataTableColumn as DataTableColumnType,
    DataTableKind,
    DataTableSize,
} from './types';
import { defaultKeyExtractor } from './utils';

export type { SortDescriptor, SortDirection } from 'react-stately';

export type DataTableRootProps = {
    /**
     * Visual variation of the table
     * @default 'default'
     */
    kind?: DataTableKind;

    /**
     * Children to render inside the root.
     */
    children: React.ReactNode;
} & SearchStateProps;

/**
 * The root component for a DataTable.
 */
export function DataTableRoot(props: DataTableRootProps) {
    const search = useSearchState(props);
    return <SearchContext.Provider value={search}>{props.children}</SearchContext.Provider>;
}

export type DataTableLayoutProps = React.ComponentPropsWithoutRef<'div'>;

export type DataTableSelection = Selection;

export const DataTableLayout = React.forwardRef(function DataTableLayout(
    props: DataTableLayoutProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    return <div ref={ref} {...props} className={clsx('flex flex-col gap-3', props.className)} />;
});

export type DataTableProps<Item extends object> = {
    /**
     * Columns to display.
     */
    columns: DataTableColumns<Item>;

    /**
     * The data to display in the table.
     */
    items: Item[];

    /**
     * Get the `href` for a row.
     * If defined, the row will be pressable and will navigate to the `href` when clicked.
     */
    getRowHref?: (item: Item) => Href | null;

    /**
     * Get the key for an item.
     * @default - Use a `key` or `id` property
     */
    getKey?: (item: Item) => Key;

    /**
     * If true, show a loading spinner.
     */
    isLoading?: boolean;

    /**
     * Blank state to display when there are no items.
     */
    emptyState?: React.ReactNode;

    /**
     * Selection actions.
     */
    selectionActions?: React.ReactNode;

    /**
     * Kind of the table.
     * @default 'default'
     */
    kind?: DataTableKind;

    /**
     * Size of the table.
     */
    size?: DataTableSize;
} & TableProps;

const SELECTION_COLUMN_KEY = '$$selection$$';

export function DataTable<Item extends object>(props: DataTableProps<Item>) {
    const {
        columns,
        items,
        className,
        getKey = defaultKeyExtractor,
        isLoading,
        emptyState,
        selectionActions,
        kind = 'default',
        size = 'medium',
        onSelectionChange,
        ...rest
    } = props;

    const kindStyle = (() => {
        switch (kind) {
            case 'minimal':
                return {
                    wrapper: '',
                    table: 'table-fixed border-spacing-0 border-0 p-0 [&_th:first-child]:pl-0 [&_th:first-child>div]:pl-0 [&_th:last-child]:pr-0',
                    column: 'border-0',
                };
            default:
                return {
                    wrapper: 'rounded-lg border border-control',
                    table: 'border-spacing-0',
                    column: 'border-b border-b-table',
                };
        }
    })();
    const hasColHeaderLabels = columns.some((column) => column.label !== '');
    const selectedKeysSet: Selection | undefined = React.useMemo(
        () => (props.selectedKeys ? new Set<Key>(props.selectedKeys) : undefined),
        [props.selectedKeys]
    );
    const defaultSelectedKeysSet = React.useMemo(
        () =>
            props.defaultSelectedKeys ? new Set<Key>(props.defaultSelectedKeys) : new Set<Key>(),
        [props.defaultSelectedKeys]
    );
    const disabledKeysSet = React.useMemo(
        () => (props.disabledKeys ? new Set<Key>(props.disabledKeys) : new Set<Key>()),
        [props.disabledKeys]
    );
    const [selectedKeys, setSelectedKeys] = useControlledState<Selection>(
        selectedKeysSet,
        defaultSelectedKeysSet,
        onSelectionChange
    );
    const isSelectable = Boolean(props.selectionMode && props.selectionMode !== 'none');
    const isEmptySelection = selectedKeys !== 'all' && selectedKeys.size === 0;

    const table = (
        <div className={clsx('w-full overflow-x-auto', kindStyle.wrapper)}>
            <Table
                className={clsx('w-full bg-base', kindStyle.table, className)}
                {...rest}
                onSelectionChange={setSelectedKeys}
                selectedKeys={selectedKeys}
            >
                <TableHeader className={hasColHeaderLabels ? undefined : 'hidden'}>
                    <SelectionColumn kind={kind} isSelectable={isSelectable} />
                    {columns.map((column) => {
                        return (
                            <DataTableColumn
                                key={column.key}
                                id={column.key}
                                alignment={column.alignment}
                                allowsSorting={column.allowsSorting}
                                isRowHeader={column.isRowHeader}
                                isSelectable={isSelectable}
                                isEmptySelection={isEmptySelection}
                                kind={kind}
                                label={column.label}
                                width={column.width}
                            />
                        );
                    })}
                </TableHeader>
                <TableBody<Item>
                    items={isLoading ? [] : items}
                    renderEmptyState={() =>
                        isLoading ? (
                            <LoadingPanel size="large" />
                        ) : (
                            (emptyState ?? (
                                <EmptyState>
                                    <Heading>No items</Heading>
                                </EmptyState>
                            ))
                        )
                    }
                    className="[&_tr:last-child_td]:border-0"
                >
                    {(item) => {
                        const rowKey = getKey(item);
                        const isSelectionDisabled = disabledKeysSet.has(rowKey);
                        const isDisabled = isSelectionDisabled && props?.disabledBehavior === 'all';
                        return (
                            <DataTableRow
                                id={rowKey}
                                // @ts-ignore bad typing for href
                                href={props.getRowHref?.(item) ?? undefined}
                                kind={kind}
                                isSelectable={isSelectable}
                                isSelectionDisabled={isSelectionDisabled}
                                isDisabled={isDisabled}
                                size={size}
                            >
                                {isSelectable && (
                                    <Cell
                                        key={SELECTION_COLUMN_KEY}
                                        className={clsx(
                                            'text-base text-ui-base',
                                            { small: 'p-1.5', medium: 'p-3' }[size]
                                        )}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                slot="selection"
                                                disabled={isSelectionDisabled}
                                            />
                                        </div>
                                    </Cell>
                                )}
                                {columns.map((column, index) => {
                                    return (
                                        <Cell
                                            key={column.key}
                                            className={clsx(
                                                'hyphens-auto break-words p-3 text-base text-ui-base',
                                                { small: 'p-1.5', medium: 'p-3' }[size],
                                                index === 0 && !isSelectable
                                                    ? { small: 'pl-2.5', medium: 'pl-5' }[size]
                                                    : null,
                                                {
                                                    start: 'text-start',
                                                    end: 'text-end',
                                                }[column.alignment ?? 'start']
                                            )}
                                        >
                                            {column.cell
                                                ? column.cell({ item, isDisabled })
                                                : (column.data?.(item) ?? item[column.key])}
                                        </Cell>
                                    );
                                })}
                            </DataTableRow>
                        );
                    }}
                </TableBody>
            </Table>
        </div>
    );

    return isSelectable ? (
        <DataTableSelection
            actions={selectionActions}
            isEmptySelection={isEmptySelection}
            selectedKeys={selectedKeys}
        >
            {table}
        </DataTableSelection>
    ) : (
        table
    );
}

const kindColumnStyle: Record<DataTableKind, string> = {
    minimal: 'border-0',
    default: 'border-b border-b-table',
};

const SelectionColumn = React.memo(function SelectionColumn(props: {
    kind: DataTableKind;
    isSelectable: boolean;
}) {
    const { isSelectable, kind } = props;
    if (!isSelectable) {
        return null;
    }
    return (
        <Column key={SELECTION_COLUMN_KEY} className={clsx('w-6 p-3', kindColumnStyle[kind])}>
            <div className="flex justify-end">{isSelectable && <Checkbox slot="selection" />}</div>
        </Column>
    );
});

const DataTableColumn = React.memo(function DataTableColumn(props: {
    id: Key;
    isSelectable: boolean;
    isEmptySelection: boolean;
    kind: DataTableKind;
    isRowHeader: DataTableColumnType<unknown>['isRowHeader'];
    allowsSorting: DataTableColumnType<unknown>['allowsSorting'];
    width: DataTableColumnType<unknown>['width'];
    label: DataTableColumnType<unknown>['label'];
    alignment: DataTableColumnType<unknown>['alignment'];
}) {
    const {
        id,
        isRowHeader,
        allowsSorting,
        width,
        label,
        isSelectable,
        isEmptySelection,
        alignment,
        kind,
    } = props;
    return (
        <Column
            id={id}
            isRowHeader={isRowHeader}
            allowsSorting={allowsSorting}
            style={{ width }}
            className={({ allowsSorting }) =>
                clsx(
                    'p-3',
                    allowsSorting && 'px-0 py-1',
                    kindColumnStyle[kind],
                    {
                        start: 'text-start',
                        end: 'justify-items-end',
                    }[alignment ?? 'start'],
                    !isSelectable && 'first-of-type:pl-5'
                )
            }
        >
            {({ sort, allowsSorting, sortDirection }) =>
                allowsSorting ? (
                    <>
                        <Button
                            kind="blank"
                            size="medium"
                            label={label}
                            onPress={() => {
                                if (allowsSorting) {
                                    sort(
                                        sortDirection === 'ascending' ? 'descending' : 'ascending'
                                    );
                                }
                            }}
                            trailingIcon={
                                sortDirection
                                    ? sortDirection === 'ascending'
                                        ? Icon.ArrowUp
                                        : Icon.ArrowDown
                                    : Icon.ChevronsVertical
                            }
                            className={clsx(
                                'border-0', // Remove border as it's invisible and to align padddings properly with cell content
                                !isEmptySelection && 'invisible'
                            )}
                        />
                    </>
                ) : (
                    <div
                        className={clsx(
                            'text-muted text-ui-action',
                            !isEmptySelection && 'invisible'
                        )}
                    >
                        {label}
                    </div>
                )
            }
        </Column>
    );
});

type DataTableSelectionProps = React.ComponentPropsWithoutRef<'div'> & {
    actions: React.ReactNode;
    isEmptySelection: boolean;
    selectedKeys: TableProps['selectedKeys'];
};

const DataTableSelection = React.forwardRef(function DataTableSelection(
    props: DataTableSelectionProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const { children, className, actions, isEmptySelection, selectedKeys, ...rest } = props;
    return (
        <div ref={ref} {...rest} className={clsx('relative w-full', className)}>
            {!isEmptySelection && selectedKeys ? (
                <Actions className="absolute top-0 right-3 left-16 flex h-11 items-center justify-end gap-4">
                    <div>
                        <span className="text-muted text-ui-small">
                            {selectedKeys === 'all' ? 'All' : Array.from(selectedKeys).length}{' '}
                            selected
                        </span>
                    </div>
                    {actions}
                </Actions>
            ) : null}
            {children}
        </div>
    );
});

export type DataTableFooterProps = React.ComponentPropsWithoutRef<'div'>;

export const DataTableFooterContext = React.createContext<
    ContextValue<DataTableFooterProps, HTMLDivElement>
>({});

export const DataTableFooter = React.forwardRef(function DataTableFooter(
    props: DataTableFooterProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    [props, ref] = useContextProps(props, ref, DataTableFooterContext);
    return (
        <PaginationContext.Provider value={{ className: 'flex-1' }}>
            <footer
                ref={ref}
                {...props}
                className={clsx(
                    'flex items-center gap-8 text-muted text-ui-small',
                    props.className
                )}
            />
        </PaginationContext.Provider>
    );
});

export type DataTableHeaderProps = React.ComponentPropsWithoutRef<'div'>;

export const DataTableHeaderContext = React.createContext<
    ContextValue<DataTableHeaderProps, HTMLDivElement>
>({});

export const DataTableHeader = React.forwardRef(function DataTableHeader(
    props: DataTableHeaderProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    [props, ref] = useContextProps(props, ref, DataTableHeaderContext);
    const search = React.useContext(SearchContext);
    return (
        <HeadlessTextInputContext.Provider
            value={
                search
                    ? {
                          value: search.searchQuery,
                          placeholder: '',
                          onChange: (event) => {
                              search.setSearchQuery(event.target.value);
                          },
                      }
                    : {}
            }
        >
            <header
                ref={ref}
                {...props}
                className={clsx('flex gap-3 rounded-md', props.className)}
            />
        </HeadlessTextInputContext.Provider>
    );
});

/**
 * A row in the DataTable
 */
function DataTableRow<T extends object>(
    props: RowProps<T> & {
        kind: DataTableKind;
        isSelectable: boolean;
        isSelectionDisabled: boolean;
        size: DataTableSize;
    }
) {
    const { href, kind, isSelectable, isSelectionDisabled, size, ...rest } = props;
    const isInteractive = !rest.isDisabled && Boolean(href);
    return (
        <Row
            id={props.id}
            href={href}
            className={({ isSelected, isFocusVisible, isHovered }) =>
                clsx(
                    'relative',
                    {
                        minimal: '[&_td:first-child]:pl-0 [&_td:last-child]:pr-0',
                        default:
                            '[&_td]:border-b [&_td]:border-b-table last:[&_td]:last:rounded-br-lg first:[&_td]:last:rounded-bl-lg',
                    }[kind],
                    isFocusVisible && 'z-10 shadow-focus-ring',
                    isHovered && 'bg-secondary',
                    isInteractive && 'cursor-pointer',
                    isSelected ? 'bg-muted' : 'bg-base'
                )
            }
            {...rest}
        />
    );
}
